import { Children, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dialog, storage } from 'app/utils';
import { useLoader, useMountEffect } from 'app/hooks';
import { Breadcrumb } from 'app/components';
import { Button } from 'app/components/common';
import { Table, ITable, IPaging } from 'app/components/data';
import { clients } from 'app/services';
import { IClient } from 'app/models';

const stateKey = 'modules.clients';
const loader = (params: IPaging) => {
  storage.set(stateKey, params);
  const { page, rows, sort } = params;

  return clients.query({ sort: sort?.field, order: sort?.order, skip: (page * rows), take: rows });
};

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statuses: Record<number, string> = useMemo(() => ({
    0: t('app.status.disabled'),
    1: t('app.status.enabled'),
    2: t('app.status.approved'),
  }), [t]);

  const { loading, data, total, params, reload } = useLoader(loader);

  const actions: Record<string, (item: IClient) => Promise<any> | void> = useMemo(() => ({
    delete: (item) => dialog.confirm({
      message: t('clients.delete.question', { ...item }),
      accept: { label: t('app.delete'), className: 'ml-2 p-button-danger' },
      reject: { label: t('app.cancel') },
    }).then((accepted) => {
      if (accepted) clients.delete(item).then(({ success }) => {
        if (success) dialog.toast({ severity: 'success', message: t(`clients.delete.success`, { ...item }) });

        return reload(params);
      });
    }),
  }), [params, reload, t]);

  const table: ITable<IClient> = { data, total, loading,
    className: ['min-h-0', 'flex-auto'],
    columns: [
      { field: 'name', type: 'string', header: t('clients.fields.name'), sortable: true },
      { field: 'status', type: 'boolean', header: { label: t('clients.fields.status'), className: 'w-6rem' },
        template: (item) => statuses[item.status],
      },
      { field: 'actions', align: 'right', header: { className: 'w-6rem' }, className: 'p-0',
        template: (item) => Children.toArray([
          <Button href={`${item.id}`} text rounded icon="mdi-edit" />,
          <Button severity="danger" text rounded icon="mdi-delete" onClick={() => actions['delete'](item)} />,
        ]),
      },
    ],
    dataKey: 'id',
    removableSort: true,
    resizableColumns: true,
    scrollable: true,
    scrollHeight: 'flex',
    selectionMode: 'single',
    paginator: { show: true, page: params?.page, rows: params?.rows, options: [20, 50, 100], className: 'border-none' },
    sort: params?.sort,

    onLoad: reload,
    onRowDoubleClick: (item) => navigate(item.id),
  };

  useMountEffect(() => {
    reload(storage.get(stateKey) ?? { page: 0, rows: 20 }).then();
  });

  return (
    <div className="h-full flex flex-column">
      <div className="flex flex-none surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text icon="mdi-cached" severity="success" className="mr-2 px-3 py-1" disabled={loading} onClick={() => reload(params)} />
          <Button href="new" icon="mdi-add" label={t('clients.new')} className="px-3 py-1" />
        </div>
      </div>
      <Table { ...table } />
    </div>
  );
});
